import { Globals } from "@coreapp/CoreApp/Globals";
import {
  COST_BAND_FIELDS,
  kEmployeeLocation,
  kEmployeePositionID,
  kEmployeeSupervisorID,
  kParentPositionID,
  kPositionFieldIDs,
  kPositionID,
  kPositionLocation,
  kReservedFields,
  restrictedInternalFields
} from "@coreapp/constants";

import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { SynthesizePositions } from "@coreapp/utils/SynthesizePositions";
import { PositionConfig } from "@coreapp/CoreApp/PositionConfig";
import {
  ChartSettingsSectionRoutes,
  getChartSettingsTestPrefix
} from "@/providers/NavigationProvider";

export const MAPPING_TEST_PREFIX = getChartSettingsTestPrefix(ChartSettingsSectionRoutes.Mapping);

export const GenerateEmptyFieldMap = chartConfig => {
  const isPositionChart = IsPositionChart(chartConfig);
  if (isPositionChart) {
    return {
      //# position fields
      PositionID: null,
      "Parent PositionID": null, //# position parentID for consistency
      "Position Location": null,
      "Position Title": null,
      "Position Division": null,
      "Desired Holders": null,
      "Position Budgeted Salary": null,
      "Position Cost Center": null,
      "Position Cost Band Minimum": null,
      "Position Cost Band Midpoint": null,
      "Position Cost Band Maximum": null,
      //# employee fields
      EmployeeID: null,
      "Employee SupervisorID": null,
      "Employee PositionID": "Employee PositionID", //# Default value
      "Employee Location": null,
      Title: null,
      Name: null,
      "First Name": null,
      "Preferred First": null,
      "Last Name": null,
      "Preferred Last": null,
      aTitle: null,
      Division: null,
      "Employee Salary": null,
      "Employee Pay": null,
      "Employee Cost Center": null
    };
  } else {
    return {
      PersonID: null,
      SupervisorID: null,
      Name: null,
      Title: null,
      aTitle: null,
      "First Name": null,
      "Preferred First": null,
      "Last Name": null,
      "Preferred Last": null,
      Division: null,
      Location: null
    };
  }
};

// need function returning just required field ids
// separate function for position and field groups (nested structure)

export const GetRequiredFields = chartConfig => {
  const isPosChart = IsPositionChart(chartConfig);

  if (isPosChart) {
    let required = [
      kPositionID,
      kParentPositionID,
      "Position Title",
      "EmployeeID",
      kEmployeeSupervisorID,
      kEmployeePositionID,
      "Name",
      "Title"
    ];

    if (!SynthesizePositions(chartConfig)) {
      // Remove employee supervisor ID if not synthesizing positions
      required = required.filter(field => field !== kEmployeeSupervisorID);
    }

    return required;
  } else {
    return ["PersonID", "SupervisorID", "Name", "Title"];
  }
};

export const GetAllOptionalFields = function (chartConfig) {
  const emptyFieldMap = GenerateEmptyFieldMap(chartConfig);
  const requiredFields = GetRequiredFields(chartConfig);
  const ids = Object.keys(emptyFieldMap);
  return ids.filter(item => !requiredFields.includes(item));
};

export const GetFilteredOptionalFields = function (chartConfig) {
  if (!chartConfig) return [];

  const allOptionalFields = GetAllOptionalFields(chartConfig);
  if (!allOptionalFields || allOptionalFields.length === 0) return [];

  const excludedFieldKeys = [];

  const isPositionChart = IsPositionChart(chartConfig);
  if (isPositionChart) {
    const fieldGroups = GetPromotedPositionEmployeeFieldGroups(chartConfig);
    if (fieldGroups) {
      if (fieldGroups.allPosFields) {
        excludedFieldKeys.push(...Object.keys(fieldGroups.allPosFields));
      } else if (fieldGroups.posFieldGroup) {
        excludedFieldKeys.push(...Object.keys(fieldGroups.posFieldGroup));
      }

      if (fieldGroups.allEmpFields) {
        excludedFieldKeys.push(...Object.keys(fieldGroups.allEmpFields));
      } else if (fieldGroups.empFieldGroup) {
        excludedFieldKeys.push(...Object.keys(fieldGroups.empFieldGroup));
      }
    }
  } else {
    const promotedFields = GetPromotedFields(chartConfig);
    if (promotedFields) {
      if (Array.isArray(promotedFields)) {
        excludedFieldKeys.push(...promotedFields);
      } else {
        excludedFieldKeys.push(...Object.keys(promotedFields));
      }
    }
  }

  const costFieldGroups = GetCostPositionEmployeeFieldGroups();
  if (costFieldGroups) {
    if (costFieldGroups.posFieldGroup) {
      excludedFieldKeys.push(...Object.keys(costFieldGroups.posFieldGroup));
    }
    if (costFieldGroups.empFieldGroup) {
      excludedFieldKeys.push(...Object.keys(costFieldGroups.empFieldGroup));
    }
    if (costFieldGroups.costBandGroup) {
      excludedFieldKeys.push(...Object.keys(costFieldGroups.costBandGroup));
    }
  }

  const uniqueExcludedKeys = [...new Set(excludedFieldKeys)];

  return allOptionalFields.filter(key => !uniqueExcludedKeys.includes(key));
};

export const GetMappedOptionalFields = (chartConfig, baseFields) => {
  if (!chartConfig) return [];
  const fieldMap = chartConfig.fields || {};
  const headers = new Set(chartConfig.headers || []);
  const claimedBaseFieldNames = new Set(
    baseFields.flatMap(id => (fieldMap[id] ? [fieldMap[id]] : []))
  );

  const fieldNameToId = GetFilteredOptionalFields(chartConfig).reduce((acc, id) => {
    const fieldName = fieldMap[id];
    if (
      fieldName &&
      headers.has(fieldName) &&
      !claimedBaseFieldNames.has(fieldName) &&
      !acc[fieldName] // first come first serve for optional fields with same name
    )
      acc[fieldName] = id;
    return acc;
  }, {});

  return Object.values(fieldNameToId);
};

export const GetPromotedFields = () => {
  return {
    PersonID: null,
    SupervisorID: null,
    Name: null,
    Title: null
  };
};

export const GetPromotedPositionEmployeeFieldGroups = chartConfig => {
  const allPosFields = {
    PositionID: null,
    "Parent PositionID": null,
    "Position Title": null,
    "Position Division": null,
    "Position Location": null,
    "Desired Holders": null
  };

  const allEmpFields = {
    EmployeeID: null,
    "Employee PositionID": null,
    "Employee SupervisorID": null,
    Name: null,
    Title: null,
    "Employee Location": null
  };

  const posFieldGroup = { ...allPosFields };
  const empFieldGroup = { ...allEmpFields };

  if (chartConfig) {
    const synthesizePositions = SynthesizePositions(chartConfig);

    if (synthesizePositions) {
      // For synthesized positions, hide this from user
      delete empFieldGroup["Employee PositionID"];
    } else {
      // For non-synthesized positions, only hide Employee SupervisorID
      delete empFieldGroup["Employee SupervisorID"];
    }
  }

  return {
    posFieldGroup,
    empFieldGroup,
    allPosFields,
    allEmpFields
  };
};

export const GetCostPositionEmployeeFieldGroups = () => {
  const posFieldGroup = {
    "Position Budgeted Salary": null,
    "Position Cost Center": null
  };
  const empFieldGroup = {
    "Employee Cost Center": null,
    "Employee Salary": null,
    "Employee Pay": null
  };
  const costBandGroup = {
    "Position Cost Band Minimum": null,
    "Position Cost Band Midpoint": null,
    "Position Cost Band Maximum": null
  };
  return { posFieldGroup, empFieldGroup, costBandGroup };
};

export const GetOptionalAuxFields = curChartObj => {
  if (!curChartObj) return false;
  let optionalFields = [];
  let mapping = curChartObj["fields"] || {};
  for (var aField in mapping) {
    if (aField.indexOf("AuxSuperID") >= 0) optionalFields.push(aField);
  }
  return optionalFields;
};

export const filterReservedFields = (fieldNameList, isPositionChart) => {
  let filtered = fieldNameList.filter(header => kReservedFields.indexOf(header) < 0).sort();

  if (isPositionChart) {
    filtered = filtered.filter(header => restrictedInternalFields.indexOf(header) < 0).sort();
  }

  return filtered;
};

const getInitialFields = (curChartObj, targetFieldID, isPositionChart, getHolderFields = false) => {
  let fieldNameList = curChartObj["headers"] || [];
  let requiredFieldIds = GetRequiredFields(curChartObj);
  let isDualTypeField = COST_BAND_FIELDS.includes(targetFieldID);

  if (isPositionChart) {
    const isPositionField = kPositionFieldIDs.includes(targetFieldID);
    if (isPositionField) {
      fieldNameList = curChartObj.positionFields;

      if (isDualTypeField && getHolderFields) {
        fieldNameList = curChartObj.holderFields;
      }
    } else {
      fieldNameList = curChartObj.holderFields;
    }

    requiredFieldIds = requiredFieldIds.filter(
      field => isPositionField === kPositionFieldIDs.includes(field)
    );
  }

  if (!fieldNameList || fieldNameList.length === 0) {
    fieldNameList = curChartObj["headers"] || [];
  }

  return { fieldNameList, requiredFieldIds };
};

export const processFieldMappings = (fieldNameList, requiredFieldIds, fieldObj, fieldMapID) => {
  const mappedValues = [];

  const costFieldGroups = GetCostPositionEmployeeFieldGroups();
  const budgetFieldIds = [];

  if (costFieldGroups) {
    if (costFieldGroups.posFieldGroup) {
      budgetFieldIds.push(...Object.keys(costFieldGroups.posFieldGroup));
    }
    if (costFieldGroups.empFieldGroup) {
      budgetFieldIds.push(...Object.keys(costFieldGroups.empFieldGroup));
    }
  }

  const uniqueMappingFields = [...requiredFieldIds, ...budgetFieldIds];

  Object.keys(fieldObj).forEach(field => {
    if (field.includes("AuxSuperID")) uniqueMappingFields.push(field);
  });

  if (uniqueMappingFields.indexOf(fieldMapID) > -1) {
    Object.keys(fieldObj).forEach(item => {
      if (uniqueMappingFields.indexOf(item) > -1 && fieldObj[item] !== null && fieldMapID != item) {
        mappedValues.push(fieldObj[item]);
      }
    });

    fieldNameList = fieldNameList.filter(item => mappedValues.indexOf(item) === -1);
  }
  return fieldNameList;
};

export const GetAvailableFields = (curChartObj, targetFieldID, getHolderFields = false) => {
  const fieldMapping = curChartObj["fields"];
  const positionConfig = new PositionConfig(curChartObj);
  const isPositionChart = positionConfig.isPositionChart();

  let { fieldNameList, requiredFieldIds } = getInitialFields(
    curChartObj,
    targetFieldID,
    isPositionChart,
    getHolderFields
  );

  fieldNameList = extendFieldsWithAppendTables(curChartObj, fieldNameList);

  if (!fieldMapping) {
    return filterReservedFields(fieldNameList, isPositionChart);
  }

  fieldNameList = processFieldMappings(
    fieldNameList,
    requiredFieldIds,
    fieldMapping,
    targetFieldID
  );

  return filterReservedFields(fieldNameList, isPositionChart);
};

function extendFieldsWithAppendTables(curChartObj, fieldNameList) {
  let extendedHeaders = [...fieldNameList];

  if (!curChartObj["append"]) return extendedHeaders;

  for (let appendIdx in curChartObj["append"]) {
    const anAppendSource = curChartObj["append"][appendIdx];

    if (anAppendSource["source"] !== "Append Table" || !anAppendSource["override"]) continue;

    const tableID = anAppendSource["override"]["tableID"];
    const tableObj = Globals._wsMgr.fOrgInfo.GetAppendTableByID(tableID);

    if (!tableObj || !tableObj["additionalFields"]) continue;

    tableObj["additionalFields"].forEach(field => {
      if (!extendedHeaders.includes(field)) {
        extendedHeaders.push(field);
      }
    });
  }

  return extendedHeaders;
}

export const GetFieldMap = chartConfig => {
  return chartConfig["fields"] ?? GenerateEmptyFieldMap(chartConfig);
};

// this check is used for displaying invalid mapped fields (not ones that haven't been set)
export const ValidMappedFieldCheck = (chartConfig, mapping, fieldId) => {
  const headers = chartConfig?.headers;
  const mappedValuesInUse = mapping
    ? Object.values(mapping)?.filter(function (item, index, array) {
        return array.indexOf(item) !== index;
      })
    : [];

  const fieldName = mapping?.[fieldId];

  const isInternallyGenerated = getInternalMappedFields(chartConfig);
  let ignoredFields = [];
  const isPositionChart = IsPositionChart(chartConfig);
  if (isPositionChart) {
    ignoredFields = ignoredFields.concat([kPositionLocation, kEmployeeLocation]);
  }

  let validMap = true;
  let errorMsg = "";
  if (!headers || ignoredFields.includes(fieldId)) return validMap;
  if (!fieldName) return "* Required field";
  else if (headers.indexOf(fieldName) === -1 && !isInternallyGenerated) {
    errorMsg = "*Field not found";
    validMap = false;
  } else if (mappedValuesInUse.indexOf(fieldName) > -1) {
    errorMsg = "*Field already assigned";
    validMap = false;
  }
  var isAuxField = fieldName?.indexOf("AuxSuperID") >= 0;
  if (isAuxField || fieldName == "Photo" || fieldName == "Desired Holders") {
    if (
      headers.indexOf(fieldName) > -1 &&
      fieldName !== "Photo" &&
      fieldName !== "Desired Holders"
    ) {
      errorMsg = "*Invalid Aux ID";
    }
  }
  return errorMsg;
};

export const getInternalMappedFields = chartConfig => {
  const isPositionChart = IsPositionChart(chartConfig);
  const synthesizePositions = SynthesizePositions(chartConfig);
  if (!isPositionChart) return [];
  if (synthesizePositions) {
    return [kEmployeePositionID, kPositionID, kParentPositionID];
  }
  return [];
};

export const GetDefaultCurrencyFieldFormat = (options = {}) => {
  return {
    type: "Number",
    breakChar: "",
    thousandsep: true,
    prefix: "$ ",
    numDecimals: "0",
    postfix: "",
    ...options
  };
};

export const GetDefaultNumberFieldFormat = (options = {}) => {
  return {
    type: "Number",
    breakChar: "",
    thousandsep: true,
    prefix: "",
    numDecimals: "0",
    postfix: "",
    ...options
  };
};

export const initializeFieldFormats = (
  fields,
  localConfig,
  onUpdate,
  formatType = "currency",
  options = {}
) => {
  if (!fields || fields.length === 0) return;

  const newFieldFormats = {};
  const getFormatFunction =
    formatType === "currency" ? GetDefaultCurrencyFieldFormat : GetDefaultNumberFieldFormat;

  for (const field of fields) {
    if (localConfig.fieldFormats && localConfig.fieldFormats[field]) {
      // respect existing formats
      continue;
    }
    newFieldFormats[field] = getFormatFunction(options);
  }

  if (Object.keys(newFieldFormats).length > 0) {
    const nextFieldFormats = {
      ...localConfig.fieldFormats,
      ...newFieldFormats
    };
    onUpdate({ fieldFormats: { ...nextFieldFormats } }, ["fieldFormats"]);
  }
};
